<template>
  <SideSheet
    :is-open="isOpen"
    position="right"
    container-class="pending-orders-sheet"
    content-size="md"
    @close="$emit('close')"
  >
    <NavBar sticky hide-border>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
    </NavBar>
    <LoaderIndicator
      v-if="isLoading"
      size="32"
      with-container
      class="pending-orders-sheet__loader"
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="pending-orders-sheet__helper"
      generic-error
      button-label="Tentar novamente"
      @click="fetchPendingOrders"
    />
    <div v-else class="pending-orders-sheet__content">
      <Heading class="pending-orders-sheet__content__title" :level="2">
        {{ pendingOrders?.title }}
      </Heading>
      <section
        v-for="(product, productIndex) in pendingOrders?.products"
        :key="productIndex"
        class="pending-orders-sheet__section"
      >
        <BaseText
          :color="NebraskaColors.elementPrimary"
          class="pending-orders-sheet__section__title"
          size="p4"
        >
          {{ product.title }}
        </BaseText>
        <button
          v-for="(order, orderIndex) in product?.orders"
          :key="orderIndex"
          class="pending-orders-sheet__section__button"
          @click="openDetails(order)"
        >
          <InitialAvatarIcon size="medium" :icon="order.icon" />
          <div>
            <BaseText :color="NebraskaColors.elementPrimary" size="p3">
              {{ order.title }}
            </BaseText>
            <BaseText
              size="p4"
              class="pending-orders-sheet__section__button__description"
            >
              {{ order.description }}
            </BaseText>
            <ChipStatus status="information" :label="order.status" />
          </div>
          <BaseText size="p4" :color="NebraskaColors.elementPrimary">
            {{ order.value }}
          </BaseText>
        </button>
      </section>
    </div>
    <ProductOrderDetails
      :is-open="isProductDetailsOpen"
      :order-id="orderId"
      :product-type="productType"
      @close="isProductDetailsOpen = false"
    />
    <PortfolioOrderDetails
      :is-open="isPortfolioDetailsOpen"
      :order-id="orderId"
      :product-type="productType"
      :portfolio-type="portfolioType"
      @close="isPortfolioDetailsOpen = false"
    />
  </SideSheet>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus,
  Heading,
  BaseText,
  InitialAvatarIcon,
  ChipStatus
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';
import { ProductOrderDetails, PortfolioOrderDetails } from './components';

import {
  getPendingOrders,
  IPendingOrder,
  IOrder,
  EOrderType
} from '@/modules/home/services/orders';

interface Props {
  isOpen: boolean;
  orderType: string;
}

const props = defineProps<Props>();

const pendingOrders = ref<IPendingOrder | null>(null);
const isLoading = ref(false);
const hasError = ref(false);

const selectedOrder = ref<IOrder | null>(null);
const isProductDetailsOpen = ref(false);
const isPortfolioDetailsOpen = ref(false);

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen) {
      fetchPendingOrders();
    }
  },
  { immediate: true }
);

const orderId = computed(() => selectedOrder.value?.id || '');
const productType = computed(() => selectedOrder.value?.productType || '');
const portfolioType = computed(() => selectedOrder.value?.portfolioType || '');

function openDetails(order: IOrder) {
  selectedOrder.value = order;

  if (order.orderType === EOrderType.PORTFOLIO) {
    isPortfolioDetailsOpen.value = true;
  } else {
    isProductDetailsOpen.value = true;
  }
}

async function fetchPendingOrders() {
  try {
    isLoading.value = true;
    hasError.value = false;

    pendingOrders.value = await getPendingOrders(props.orderType);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>

<style lang="less" scoped>
@height: calc(100vh - 64px);

.pending-orders-sheet {
  &__loader {
    height: @height;
  }

  &__helper {
    height: @height;
    margin: 0 auto;
    justify-content: center;
  }

  &__content {
    padding: @size-spacing-x1000 @size-spacing-x1200;

    &__title {
      margin-bottom: @size-spacing-x1000;
    }
  }

  &__section {
    margin-bottom: @size-spacing-x700;

    &__button {
      width: 100%;
      display: flex;
      gap: @size-spacing-x350;
      padding: @size-spacing-x400 0;
      border-top: @size-border-x400 solid @divider-primary;

      &__description {
        margin-bottom: @size-spacing-x150;
      }

      &:first-of-type {
        border-top: none;
      }

      > *:nth-child(1),
      > *:nth-child(3) {
        flex-shrink: 0;
      }

      > *:nth-child(2) {
        flex-grow: 1;
        text-align: left;
      }
    }
  }
}
</style>
